/**
 * The normative error catalogue — API_CONTRACT.md v1.2 §8.7.
 *
 * `errors.catalogue.json` is authoritative: it defines the code set, the
 * namespace list and the normative count. No prose figure in any document is
 * authoritative, and the count is derived, never asserted.
 *
 * Codes are partitioned into two disjoint sets:
 *   `codes[]`             approved — explicitly enumerated by API_CONTRACT.md.
 *                         These are the ONLY codes the API may emit.
 *   `founder_required[]`  declared by v1.0's withdrawn per-namespace subtotals
 *                         but never enumerated. They carry no code name, so
 *                         they are structurally unable to be emitted.
 *
 * Nothing here was invented. The 36 founder_required gaps are recorded as
 * namespace-level counts and await founder approval (BLK-6, resolved to
 * Option C; authoring the codes themselves is deferred).
 */
import catalogue from './errors.catalogue.json' with { type: 'json' };
export const ERROR_CATALOGUE = catalogue;
const BY_CODE = new Map(ERROR_CATALOGUE.codes.map((entry) => [entry.code, entry]));
/** Every code in the catalogue, sorted. */
export const ERROR_CODES = Object.freeze(ERROR_CATALOGUE.codes.map((e) => e.code));
/**
 * The 29 namespaces declared by API_CONTRACT.md v1.2 §8.3, read from the
 * catalogue rather than restated here — the catalogue is the source of truth.
 */
export const DECLARED_NAMESPACES = Object.freeze([
    ...ERROR_CATALOGUE.namespaces.declared,
]);
/**
 * Namespace-level gaps declared by v1.0's withdrawn subtotals and never
 * enumerated. These carry NO code name, so they cannot be looked up, thrown or
 * serialised — they are unemittable by construction, not by policy.
 */
export const FOUNDER_REQUIRED = Object.freeze([
    ...ERROR_CATALOGUE.founder_required,
]);
/** Derived counts. `approved` is the normative code count (§8.7). */
export const ERROR_CATALOGUE_COUNTS = ERROR_CATALOGUE.counts;
export function isErrorCode(code) {
    return BY_CODE.has(code);
}
export function lookupErrorCode(code) {
    return BY_CODE.get(code);
}
/**
 * Throws if the code is absent. Used where emitting an uncatalogued code would
 * be a contract breach rather than a runtime condition.
 */
export function requireErrorCode(code) {
    const entry = BY_CODE.get(code);
    if (entry === undefined) {
        throw new Error(`Error code "${code}" is not in the catalogue (API_CONTRACT.md §8.7)`);
    }
    return entry;
}
//# sourceMappingURL=errors.js.map